import React from "react";
import {
  Grid,
  Typography,
  Box,
  Container
} from "@mui/material";
import {
  AttachMoney,
  AccountBalance,
  CreditCard,
  Favorite,
  Security,
  DirectionsCar,
  Home,
  AccountBalanceWallet,
  Business,
  MonetizationOn,
  TrendingUp,
  AccountTree,
  Savings,
} from "@mui/icons-material";
import FAQSection from "./FAQSection";

import Layout from "../components/Layout/Layout";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Fade } from "react-reveal";
import Animated3DCard from "./Animated3DCard";

const informations = [
  { id: 1, title: "Mutual fund", description: "Mutual funds provide diversification and professional management, offering growth opportunities while aligning with your financial goals and risk tolerance.", icon: <AttachMoney fontSize="large" />, iconColor: "#ff9800" },
  { id: 2, title: "Demat Trading", description: "A demat account stores shares and securities electronically, while a trading account allows buying and selling of shares on exchanges. Both are essential for seamless investment management.", icon: <AccountBalance fontSize="large" />, iconColor: "#3f51b5" },
  { id: 3, title: "FD/Post Office", description: "FDs and Post Office schemes offer safe and reliable investment options, providing guaranteed returns with varying interest rates and flexible tenures to suit your financial goals.", icon: <Savings fontSize="large" />, iconColor: "#4caf50" },
  { id: 4, title: "Debit/Credit Cards", description: "Debit cards draw from your bank account balance, while credit cards offer a line of credit with interest charged on borrowed amounts if not paid within the grace period.", icon: <CreditCard fontSize="large" />, iconColor: "#9c27b0" },
  { id: 5, title: "Health Insurance", description: "Health insurance helps cover medical expenses, ensuring access to necessary healthcare and financial protection against unexpected health costs.", icon: <Favorite fontSize="large" />, iconColor: "#e91e63" },
  { id: 6, title: "Life Insurance", description: "Life insurance provides financial protection to your loved ones by offering a payout in case of your untimely death, helping secure their future.", icon: <Security fontSize="large" />, iconColor: "#607d8b" },
  { id: 7, title: "Public Provident Fund", description: "The Public Provident Fund (PPF) is a government-backed, long-term savings scheme offering tax benefits and guaranteed returns, ideal for secure retirement planning.", icon: <AccountBalanceWallet fontSize="large" />, iconColor: "#ff5722" },
  { id: 8, title: "Employee Provident/Pension Fund", description: "The Employee Provident Fund (EPF) and Pension Fund provide employees with a secure savings and retirement plan, offering tax benefits and long-term financial security.", icon: <Business fontSize="large" />, iconColor: "#009688" },
  { id: 9, title: "Vehicle Details", description: "Vehicle details provide essential information about ownership, insurance, and maintenance, helping manage and protect your investment in automobiles.", icon: <DirectionsCar fontSize="large" />, iconColor: "#f44336" },
  { id: 10, title: "Bond Details", description: "Bond details include information about the bond type, interest rates, maturity date, and issuer, helping investors manage their fixed-income investments effectively.", icon: <MonetizationOn fontSize="large" />, iconColor: "#8bc34a" },
  { id: 11, title: "Property Details", description: "Property details help track ownership, value, and legal status of real estate, ensuring proper management and investment decisions for long-term wealth.", icon: <Home fontSize="large" />, iconColor: "#795548" },
  { id: 12, title: "NPS Account", description: "Property details help track ownership, value, and legal status of real estate, ensuring proper management and investment decisions for long-term wealth.", icon: <AccountTree fontSize="large" />, iconColor: "#673ab7" },
  { id: 13, title: "Gold and Other Assets", description: "Gold and other assets provide a secure investment avenue, offering diversification and protection against inflation while contributing to long-term wealth growth.", icon: <TrendingUp fontSize="large" />, iconColor: "#ffc107" },
];

const Informations = () => {
  return (
    <Layout>
      <HelmetProvider>
        <Helmet>
          <title>Finkoda | Information</title>
          <meta name="description" content="Mutual funds, demat trading, insurance information, investment accounts, property, bond information, gold, vehicle information, and basic financial data are all safely stored in our app. You can quickly access and safeguard your data anytime you need it." />
          <meta name="keywords" content="financial services, mutual funds, demat trading, insurance details, investment accounts, property management, bond details, gold investment, vehicle finance, secure financial information, financial planning, asset management" />
          <link rel="canonical" href="https://finkoda.com/information" />
          <link rel="alternate" media="only screen and (max-width: 640px)" href="https://finkoda.com/information" />
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Our Services - Secure Financial Information Management" />
          <meta property="og:url" content="https://finkoda.com/information" />
          <meta property="og:image" content="%PUBLIC_URL%/logo.png" />
          <meta property="og:description" content="Our app securely stores all your essential financial information, including mutual funds, demat trading, insurance details, investment accounts, property, bonds, gold, vehicle information, and general financial data. Your data is safe and easily accessible whenever you need it." />

          {/* Twitter */}
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content="Our Services - Secure Financial Information Management" />
          <meta name="twitter:site" content="@Finkoda" />
          <meta name="twitter:description" content="Easily manage and store your essential financial data, including mutual funds, insurance, investments, property, bonds, gold, and vehicles. Your information is secure and accessible anytime." />
          <meta name="twitter:image" content="%PUBLIC_URL%/logo.png" />
          <meta name="twitter:image:alt" content="Finkoda" />


        </Helmet>
      </HelmetProvider>
      <Box sx={{ py: 5 }}>
        <Box sx={{ textAlign: "center", maxWidth: 800, mx: "auto", mb: 5 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: {
                xs: "1.5rem", // mobile
                sm: "2rem",   // small tablets
                md: "2.5rem", // desktops
              },
              fontWeight: "bold",
              mb: 3,
              color: "#FFF",
            }}
          >
            Secure Your Financial Future with Expert Consulting
          </Typography>
          <Typography variant="body1" sx={{ color: "#FFF" }}>
            Our platform securely stores and manages essential financial information, ensuring easy access and organization.
          </Typography>
        </Box>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {informations.map((information, index) => (
              <Grid item xs={12} sm={6} md={4} key={information.id} sx={{ display: "flex" }}>
                <Fade bottom delay={index * 100}>

                  <Animated3DCard
                    icon={information.icon}
                    iconColor={information.iconColor}
                    title={information.title}
                    description={information.description}
                  />

                </Fade>
              </Grid>
            ))}
          </Grid>

        </Container>
      </Box>

      <FAQSection />
    </Layout>
  );
};

export default Informations;
